import React, { useState, useEffect } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { I18n } from 'react-redux-i18n'

import { updateActionsErrors } from 'redux/smartchat/details/actions'
import { setAlternativesActionsErrors } from 'redux/smartchat/alternatives/actions'
import TimeOutViewUI from './TimeOutViewUI'
import { getDataOptions } from 'components/SmartChatView/utils'

const TimeOutView = ({ renderType, withPetition, withProcess, label, placeholder, actionType, dataWay, currentValue, onChange, error }) => {
  const { header, modalActionRef } = useSelector(store => ({
    header: store.smartchatHeader.header,
    modalActionRef: dataWay?.type === 'alternatives'
      ? store.smartchatAlternatives.alternativesModalActionRef.find(modalActionRef => modalActionRef.alternativesId === dataWay.alternativesId) || {}
      : store.smartchatDetails.modalActionRef
  }), shallowEqual)
  const dispatch = useDispatch()
  const [processesOptions, setProcessesOptions] = useState(null)
  const [value, setValue] = useState({})
  const [dataToExport, setDataToExport] = useState(currentValue || {})
  const [loadingProcessesOptions, setLoadingProcessesOptions] = useState(false)
  const [previewIntegrity, setPreviewIntegrity] = useState({})
  const timerPetitionsOptions = [
    { value: 1, label: I18n.t('smartchat_timeout_first') },
    { value: 2, label: I18n.t('smartchat_timeout_second') }
  ]

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed && withProcess && !processesOptions) getProcessesOptions()

    return () => isSubscribed = false
  }, [])

  // Current data
  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed) {
      if (!currentValue || !Object.keys(currentValue).length) return

      if (renderType === 'action') {
        if (modalActionRef.mode !== 'update') return
        onChange(currentValue)
      }

      const error = { [actionType]: {} }
      const updatedValue = { ...value }
      const updatedPreviewIntegrity = { ...previewIntegrity }

      // Timeout petition
      if (withPetition) {
        const option = timerPetitionsOptions.find(petition => petition.value === currentValue.repetitionNumber)
        if (option) {
          if (updatedPreviewIntegrity.petition) updatedPreviewIntegrity.petition = null
          updatedValue.petition = option
        } else {
          // Error
          if (renderType === 'action') {
            if (updatedValue.petition !== null) error[actionType].petition = I18n.t('smartchat_tooltip_warning_notFound')
          } else {
            updatedPreviewIntegrity.petition = I18n.t('smartchat_tooltip_warning_notFound')
          }
        }
      }

      // Timeout
      if (currentValue.time && currentValue.unit) {
        if (updatedPreviewIntegrity.timer) updatedPreviewIntegrity.timer = null
        updatedValue.timer = { time: currentValue.time, unit: currentValue.unit }
      } else {
        // Error
        if (renderType === 'action') {
          if (updatedValue.timer !== null) error[actionType].timer = I18n.t('smartchat_tooltip_warning_notFound')
        } else {
          updatedPreviewIntegrity.timer = I18n.t('smartchat_tooltip_warning_notFound')
        }
      }

      // Process
      if (withProcess && processesOptions?.length) {
        const option = processesOptions.find(process => process.value === currentValue.processId)
        if (option) {
          if (updatedPreviewIntegrity.process) updatedPreviewIntegrity.process = null
          updatedValue.process = option
        } else {
          // Error
          if (renderType === 'action') {
            if (updatedValue.process !== null) error[actionType].process = I18n.t('smartchat_tooltip_warning_notFound')
          } else {
            updatedPreviewIntegrity.process = I18n.t('smartchat_tooltip_warning_notFound')
          }
        }
      }

      // Update local data
      setValue(updatedValue)

      // Error reports
      if (renderType === 'action') {
        if (Object.keys(error[actionType]).length) dataWay?.type === 'alternatives'
          ? dispatch(setAlternativesActionsErrors({ alternativesId: dataWay.alternativesId, actionsErrors: error }))
          : dispatch(updateActionsErrors(error))
      } else {
        if (Object.keys(updatedPreviewIntegrity).length) setPreviewIntegrity(updatedPreviewIntegrity)
      }
    }

    return () => isSubscribed = false
  }, [currentValue, processesOptions])

  const getProcessesOptions = async () => {
    setLoadingProcessesOptions(true)
    try {
      const { processesOptions } = await getDataOptions({ requestedData: ['processesOptions'], params: { headerId: header._id } })
      setProcessesOptions(processesOptions)
    } finally {
      setLoadingProcessesOptions(false)
    }
  }

  const onChangeInner = ({ fieldType, fieldData }) => {
    const updatedValue = { ...value }
    const updatedDataToExport = { ...dataToExport }

    // Data to export
    if (fieldType === 'petition') {
      if (fieldType) {
        updatedValue.petition = fieldData
        updatedDataToExport.repetitionNumber = fieldData.value
      } else {
        // Clear later fields and data
        if (updatedValue.timer) {
          updatedValue.timer = null
          updatedDataToExport.time = null
          updatedDataToExport.unit = null
        }
        if (withProcess && updatedValue.process) {
          updatedValue.process = null
          updatedDataToExport.processId = null
        }
      }
    }
    if (fieldType === 'timer') {
      if (fieldData.time) {
        updatedValue.timer = fieldData
        updatedDataToExport.time = fieldData.time
        updatedDataToExport.unit = fieldData.unit
      } else {
        updatedValue.timer = null
        updatedDataToExport.time = null
        updatedDataToExport.unit = null
        // Clear later fields and data
        if (withProcess && updatedValue.process) {
          updatedValue.process = null
          updatedDataToExport.processId = null
        }
      }
    }
    if (fieldType === 'process' && fieldData) {
      updatedValue.process = fieldData
      updatedDataToExport.processId = fieldData.value
    }

    // Update local data
    setValue(updatedValue)
    setDataToExport(updatedDataToExport)

    // Export the data
    onChange(updatedDataToExport)
  }

  return (
    <TimeOutViewUI
      renderType={renderType}
      withPetition={withPetition}
      withProcess={withProcess}
      label={label}
      placeholder={placeholder}
      timerPetitionsOptions={timerPetitionsOptions}
      processesOptions={processesOptions}
      value={value}
      onChangeInner={onChangeInner}
      loadingProcessesOptions={loadingProcessesOptions}
      error={error}
      previewIntegrity={previewIntegrity}
    />
  )
}

export default TimeOutView
